import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, MapPinned, CalendarDays, Wallet, Users, ClipboardCheck, TrendingUp, Building2, Smartphone, LogOut, Menu, FileText, Package, Camera } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/Brand";
import { NotificationBell } from "@/components/NotificationBell";
import { RoleBadge } from "@/components/Badges";

const MENU = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/admin/map", label: "Live Map", icon: MapPinned, id: "map" },
  { to: "/admin/attendance", label: "Attendance Register", icon: CalendarDays, id: "attendance" },
  { to: "/admin/attendance-log", label: "Login / Logout Log", icon: Camera, id: "attendance-log" },
  { to: "/admin/payroll", label: "Payroll Processing", icon: Wallet, id: "payroll" },
  { to: "/admin/tasks", label: "Tasks & Tickets", icon: ClipboardCheck, id: "tasks" },
  { to: "/admin/leads", label: "Sales Pipeline", icon: TrendingUp, id: "leads" },
  { to: "/admin/quotations", label: "Quotations", icon: FileText, id: "quotations" },
  { to: "/admin/products", label: "Product Catalogue", icon: Package, id: "products" },
  { to: "/admin/clients", label: "Clients / Hospitals", icon: Building2, id: "clients" },
  { to: "/admin/staff", label: "Staff Directory", icon: Users, id: "staff" },
];

const SideNav = ({ onNav }) => {
  const { user, logout } = useAuth();
  return (
    <div className="h-full flex flex-col bg-slate-900 text-slate-300">
      <div className="px-5 h-20 flex items-center border-b border-slate-800"><BrandMark light /></div>
      <div className="px-5 pt-4 pb-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
        {user.role === "director" ? "Director Console" : "HR Portal"}
      </div>
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {MENU.map(({ to, end, label, icon: Icon, id }) => (
          <NavLink key={to} to={to} end={end} onClick={onNav} data-testid={`sidebar-nav-${id}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-sky-600 text-white" : "hover:bg-slate-800 hover:text-white"}`}>
            <Icon className="h-4 w-4" />{label}
          </NavLink>
        ))}
        <Link to="/app" onClick={onNav} data-testid="sidebar-field-app-link" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-800 hover:text-white transition-colors">
          <Smartphone className="h-4 w-4" />My Field App
        </Link>
      </nav>
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-700 grid place-items-center text-white font-bold text-sm">{user.full_name?.[0]}</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white font-semibold truncate" data-testid="sidebar-user-name">{user.full_name}</div>
            <RoleBadge role={user.role} />
          </div>
          <button onClick={logout} data-testid="logout-button" className="h-9 w-9 grid place-items-center rounded-lg hover:bg-slate-800 transition-colors" title="Log out">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-30"><SideNav /></aside>
      <header className="sticky top-0 z-20 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-3">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden h-10 w-10 grid place-items-center rounded-lg hover:bg-slate-100" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64 border-0"><SideNav onNav={() => setOpen(false)} /></SheetContent>
          </Sheet>
          <div className="lg:hidden"><BrandMark compact /></div>
          <div className="hidden lg:block text-xs uppercase tracking-[0.2em] text-slate-500">Varanasi · Uttar Pradesh</div>
        </div>
        <NotificationBell />
      </header>
      <main className="px-4 lg:px-8 py-6 lg:py-8 max-w-[1500px]"><Outlet /></main>
    </div>
  );
}
